import React, { useEffect, useId, useState } from 'react';
import Dialog from '../../common/Dialog';
import type { LegalAuthorityCode, FamilyPortalUser } from '../../../dtos/portal/familyPortalAdmin.dto';
import { LEGAL_AUTHORITY_CODES, LEGAL_AUTHORITY_LABELS, isLegalAuthorityCode } from './familyPortalText';

interface ApproveDialogProps {
  open: boolean;
  portalUser: FamilyPortalUser | null;
  /** Prefill from the linked patient_guardians row when the portal row has none */
  defaultRelationship?: string;
  defaultLegalAuthority?: string;
  busy: boolean;
  onConfirm: (relationship: string, legalAuthority: LegalAuthorityCode) => void;
  onCancel: () => void;
}

/** Approve (or re-approve) a guardian: confirm relationship and legal authority (FAM-04). */
const ApproveDialog: React.FC<ApproveDialogProps> = ({
  open,
  portalUser,
  defaultRelationship,
  defaultLegalAuthority,
  busy,
  onConfirm,
  onCancel,
}) => {
  const id = useId();
  const [relationship, setRelationship] = useState('');
  const [authority, setAuthority] = useState<LegalAuthorityCode | ''>('');

  useEffect(() => {
    if (!open || !portalUser) return;
    setRelationship(portalUser.relationship || defaultRelationship || '');
    const auth = portalUser.legalAuthority || defaultLegalAuthority;
    setAuthority(isLegalAuthorityCode(auth) ? auth : '');
  }, [open, portalUser, defaultRelationship, defaultLegalAuthority]);

  if (!portalUser) return null;
  const valid = relationship.trim().length > 0 && authority !== '';
  const reapprove = portalUser.status === 'ENDED';

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (valid && !busy && authority) onConfirm(relationship.trim(), authority);
  };

  return (
    <Dialog open={open} onClose={busy ? () => undefined : onCancel} labelledBy={`${id}-title`}>
      <form onSubmit={submit} className="text-left">
        <h3 id={`${id}-title`} className="pr-6 text-lg font-semibold text-slate-800">
          {reapprove ? 'Re-approve guardian access' : 'Approve guardian access'}
        </h3>
        <p className="mt-1 text-sm text-slate-700">
          {portalUser.firstName} {portalUser.lastName} ({portalUser.email}) verified the patient’s details. Confirm their
          relationship and legal authority before they can see the record.
        </p>
        <label htmlFor={`${id}-rel`} className="mt-4 block text-sm font-medium text-slate-700">
          Relationship <span className="text-red-600" aria-hidden="true">*</span>
        </label>
        <input
          id={`${id}-rel`}
          type="text"
          value={relationship}
          onChange={(e) => setRelationship(e.target.value)}
          required
          aria-required="true"
          disabled={busy}
          className="mt-1 w-full rounded-md border border-slate-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
        />
        <label htmlFor={`${id}-auth`} className="mt-3 block text-sm font-medium text-slate-700">
          Legal authority <span className="text-red-600" aria-hidden="true">*</span>
        </label>
        <select
          id={`${id}-auth`}
          value={authority}
          onChange={(e) => setAuthority(e.target.value as LegalAuthorityCode | '')}
          required
          aria-required="true"
          disabled={busy}
          className="mt-1 w-full rounded-md border border-slate-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
        >
          <option value="">Select...</option>
          {LEGAL_AUTHORITY_CODES.map((code) => (
            <option key={code} value={code}>
              {LEGAL_AUTHORITY_LABELS[code]}
            </option>
          ))}
        </select>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!valid || busy}
            className="rounded-md bg-slate-800 px-4 py-2 text-sm text-white hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? 'Approving...' : reapprove ? 'Re-approve' : 'Approve'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

export default ApproveDialog;
